import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

// ========================================
// ACTIONS - Crew Profiles List
// ========================================
const requested = createAction("crewProfiles/requested")
const received = createAction("crewProfiles/received")
const failed = createAction("crewProfiles/failed")
const cleaned = createAction("crewProfiles/cleaned")

// ========================================
// ACTIONS - Single Profile
// ========================================
const profileRequested = createAction("crewProfiles/profile/requested")
const profileReceived = createAction("crewProfiles/profile/received")
const profileFailed = createAction("crewProfiles/profile/failed")

// ========================================
// ACTIONS - Update Profile
// ========================================
const updateRequested = createAction("crewProfiles/update/requested")
const updateReceived = createAction("crewProfiles/update/received")
const updateFailed = createAction("crewProfiles/update/failed")

// ========================================
// ACTIONS - Staff Bookings
// ========================================
const bookingsRequested = createAction("crewProfiles/bookings/requested")
const bookingsReceived = createAction("crewProfiles/bookings/received")
const bookingsFailed = createAction("crewProfiles/bookings/failed")

// ========================================
// ACTIONS - Availability
// ========================================
const availabilityRequested = createAction("crewProfiles/availability/requested")
const availabilityReceived = createAction("crewProfiles/availability/received")
const availabilityFailed = createAction("crewProfiles/availability/failed")

const availabilityUpdateRequested = createAction("crewProfiles/availability/update/requested")
const availabilityUpdateReceived = createAction("crewProfiles/availability/update/received")
const availabilityUpdateFailed = createAction("crewProfiles/availability/update/failed")

// ========================================
// ACTIONS - Scheduled Time Off
// ========================================
const timeOffRequested = createAction("crewProfiles/timeOff/requested")
const timeOffReceived = createAction("crewProfiles/timeOff/received")
const timeOffFailed = createAction("crewProfiles/timeOff/failed")

const singleTimeOffRequested = createAction("crewProfiles/timeOff/single/requested")
const singleTimeOffReceived = createAction("crewProfiles/timeOff/single/received")
const singleTimeOffFailed = createAction("crewProfiles/timeOff/single/failed")

const createTimeOffRequested = createAction("crewProfiles/timeOff/create/requested")
const createTimeOffReceived = createAction("crewProfiles/timeOff/create/received")
const createTimeOffFailed = createAction("crewProfiles/timeOff/create/failed")

const updateTimeOffRequested = createAction("crewProfiles/timeOff/update/requested")
const updateTimeOffReceived = createAction("crewProfiles/timeOff/update/received")
const updateTimeOffFailed = createAction("crewProfiles/timeOff/update/failed")

const deleteTimeOffRequested = createAction("crewProfiles/timeOff/delete/requested")
const deleteTimeOffReceived = createAction("crewProfiles/timeOff/delete/received")
const deleteTimeOffFailed = createAction("crewProfiles/timeOff/delete/failed")

// ========================================
// ACTIONS - Assigned Experiences
// ========================================
const assignedExperiencesRequested = createAction("crewProfiles/assignedExperiences/requested")
const assignedExperiencesReceived = createAction("crewProfiles/assignedExperiences/received")
const assignedExperiencesFailed = createAction("crewProfiles/assignedExperiences/failed")

const assignExperienceRequested = createAction("crewProfiles/assignedExperiences/assign/requested")
const assignExperienceReceived = createAction("crewProfiles/assignedExperiences/assign/received")
const assignExperienceFailed = createAction("crewProfiles/assignedExperiences/assign/failed")

const removeExperienceRequested = createAction("crewProfiles/assignedExperiences/remove/requested")
const removeExperienceReceived = createAction("crewProfiles/assignedExperiences/remove/received")
const removeExperienceFailed = createAction("crewProfiles/assignedExperiences/remove/failed")

const checkTripBookingConflictsRequested = createAction("crewProfiles/assignedExperiences/checkConflicts/requested")
const checkTripBookingConflictsReceived = createAction("crewProfiles/assignedExperiences/checkConflicts/received")
const checkTripBookingConflictsFailed = createAction("crewProfiles/assignedExperiences/checkConflicts/failed")

// ========================================
// ACTIONS - Documents (Licenses & Certifications)
// ========================================
const documentsRequested = createAction("crewProfiles/documents/requested")
const documentsReceived = createAction("crewProfiles/documents/received")
const documentsFailed = createAction("crewProfiles/documents/failed")

const singleDocumentRequested = createAction("crewProfiles/documents/single/requested")
const singleDocumentReceived = createAction("crewProfiles/documents/single/received")
const singleDocumentFailed = createAction("crewProfiles/documents/single/failed")

const createDocumentRequested = createAction("crewProfiles/documents/create/requested")
const createDocumentReceived = createAction("crewProfiles/documents/create/received")
const createDocumentFailed = createAction("crewProfiles/documents/create/failed")

const updateDocumentRequested = createAction("crewProfiles/documents/update/requested")
const updateDocumentReceived = createAction("crewProfiles/documents/update/received")
const updateDocumentFailed = createAction("crewProfiles/documents/update/failed")

const deleteDocumentRequested = createAction("crewProfiles/documents/delete/requested")
const deleteDocumentReceived = createAction("crewProfiles/documents/delete/received")
const deleteDocumentFailed = createAction("crewProfiles/documents/delete/failed")

// ========================================
// ACTIONS - Deactivation/Deletion Operations
// ========================================
const checkDeactivationRequested = createAction("crewProfiles/checkDeactivation/requested")
const checkDeactivationReceived = createAction("crewProfiles/checkDeactivation/received")
const checkDeactivationFailed = createAction("crewProfiles/checkDeactivation/failed")

const checkDeletionRequested = createAction("crewProfiles/checkDeletion/requested")
const checkDeletionReceived = createAction("crewProfiles/checkDeletion/received")
const checkDeletionFailed = createAction("crewProfiles/checkDeletion/failed")

const deactivateCrewRequested = createAction("crewProfiles/deactivate/requested")
const deactivateCrewReceived = createAction("crewProfiles/deactivate/received")
const deactivateCrewFailed = createAction("crewProfiles/deactivate/failed")

const activateCrewRequested = createAction("crewProfiles/activate/requested")
const activateCrewReceived = createAction("crewProfiles/activate/received")
const activateCrewFailed = createAction("crewProfiles/activate/failed")

const deleteCrewRequested = createAction("crewProfiles/delete/requested")
const deleteCrewReceived = createAction("crewProfiles/delete/received")
const deleteCrewFailed = createAction("crewProfiles/delete/failed")

// ========================================
// REDUCER
// ========================================
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  metadata: PAGINATION_INITIAL_STATE,

  // Single profile state
  profile: {
    ...LOADING_INITIAL_STATE,
    data: null
  },

  // Update state
  update: {
    ...LOADING_INITIAL_STATE
  },

  // Staff Bookings
  bookings: {
    ...LOADING_INITIAL_STATE,
    data: []
  },

  // Availability
  availability: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  availabilityUpdate: {
    ...LOADING_INITIAL_STATE
  },

  // Scheduled Time Off
  timeOff: {
    ...LOADING_INITIAL_STATE,
    data: [],
    metadata: PAGINATION_INITIAL_STATE
  },
  singleTimeOff: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  createTimeOff: {
    ...LOADING_INITIAL_STATE
  },
  updateTimeOff: {
    ...LOADING_INITIAL_STATE
  },
  deleteTimeOff: {
    ...LOADING_INITIAL_STATE
  },

  // Assigned Experiences
  assignedExperiences: {
    ...LOADING_INITIAL_STATE,
    data: [],
    metadata: {}
  },
  assignExperience: {
    ...LOADING_INITIAL_STATE
  },
  removeExperience: {
    ...LOADING_INITIAL_STATE
  },
  tripBookingConflicts: {
    ...LOADING_INITIAL_STATE,
    data: null
  },

  // Documents (Licenses & Certifications)
  documents: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  singleDocument: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  createDocument: {
    ...LOADING_INITIAL_STATE
  },
  updateDocument: {
    ...LOADING_INITIAL_STATE
  },
  deleteDocument: {
    ...LOADING_INITIAL_STATE
  },

  // Deactivation/Deletion/Activation Operations
  checkDeactivation: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  checkDeletion: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  deactivateCrew: {
    ...LOADING_INITIAL_STATE
  },
  activateCrew: {
    ...LOADING_INITIAL_STATE
  },
  deleteCrew: {
    ...LOADING_INITIAL_STATE
  }
}

const crewProfilesReducer = createReducer(initialState, {
  // ========================================
  // Crew Profiles List
  // ========================================
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, { payload }) => {
    setReceived(state)
    state.items = payload.data
    Object.assign(state.metadata, payload.metadata)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,

  // ========================================
  // Single Profile
  // ========================================
  [profileRequested.type]: (state) => {
    setRequested(state.profile)
  },
  [profileReceived.type]: (state, { payload }) => {
    setReceived(state.profile)
    state.profile.data = payload.data
    // Store availabilities from profile response
    if (payload.data?.availabilities) {
      state.availability.data = payload.data.availabilities
    }
  },
  [profileFailed.type]: (state) => {
    setFailed(state.profile)
  },

  // ========================================
  // Update Profile
  // ========================================
  [updateRequested.type]: (state) => {
    setRequested(state.update)
  },
  [updateReceived.type]: (state, { payload }) => {
    setReceived(state.update)
    state.profile.data = payload.data
    // Update availabilities from profile response
    if (payload.data?.availabilities) {
      state.availability.data = payload.data.availabilities
    }
  },
  [updateFailed.type]: (state) => {
    setFailed(state.update)
  },

  // ========================================
  // Staff Bookings
  // ========================================
  [bookingsRequested.type]: (state) => {
    setRequested(state.bookings)
  },
  [bookingsReceived.type]: (state, { payload }) => {
    setReceived(state.bookings)
    state.bookings.data = payload
  },
  [bookingsFailed.type]: (state) => {
    setFailed(state.bookings)
  },

  // ========================================
  // Availability
  // ========================================
  [availabilityRequested.type]: (state) => {
    setRequested(state.availability)
  },
  [availabilityReceived.type]: (state, { payload }) => {
    setReceived(state.availability)
    state.availability.data = Array.isArray(payload) ? payload : [payload]
  },
  [availabilityFailed.type]: (state) => {
    setFailed(state.availability)
  },

  [availabilityUpdateRequested.type]: (state) => {
    setRequested(state.availabilityUpdate)
  },
  [availabilityUpdateReceived.type]: (state, { payload }) => {
    setReceived(state.availabilityUpdate)
    // API always returns availabilities array
    if (payload.data?.availabilities) {
      state.availability.data = payload.data.availabilities
    }
  },
  [availabilityUpdateFailed.type]: (state) => {
    setFailed(state.availabilityUpdate)
  },

  // ========================================
  // Scheduled Time Off
  // ========================================
  [timeOffRequested.type]: (state) => {
    setRequested(state.timeOff)
  },
  [timeOffReceived.type]: (state, { payload }) => {
    setReceived(state.timeOff)
    state.timeOff.data = Array.isArray(payload?.data) ? payload.data : []
    Object.assign(state.timeOff.metadata, payload.metadata)
  },
  [timeOffFailed.type]: (state) => {
    setFailed(state.timeOff)
  },

  [singleTimeOffRequested.type]: (state) => {
    setRequested(state.singleTimeOff)
  },
  [singleTimeOffReceived.type]: (state, { payload }) => {
    setReceived(state.singleTimeOff)
    state.singleTimeOff.data = payload
  },
  [singleTimeOffFailed.type]: (state) => {
    setFailed(state.singleTimeOff)
  },

  [createTimeOffRequested.type]: (state) => {
    setRequested(state.createTimeOff)
  },
  [createTimeOffReceived.type]: (state, { payload }) => {
    setReceived(state.createTimeOff)
    // Add new time-off to the list
    if (Array.isArray(state.timeOff.data)) {
      state.timeOff.data.unshift(payload)
    } else {
      state.timeOff.data = [payload]
    }
  },
  [createTimeOffFailed.type]: (state) => {
    setFailed(state.createTimeOff)
  },

  [updateTimeOffRequested.type]: (state) => {
    setRequested(state.updateTimeOff)
  },
  [updateTimeOffReceived.type]: (state, { payload }) => {
    setReceived(state.updateTimeOff)
    // Update the time-off in the list
    if (Array.isArray(state.timeOff.data)) {
      const index = state.timeOff.data.findIndex((item) => item.id === payload.id)
      if (index !== -1) {
        state.timeOff.data[index] = payload
      }
    }
    // Also update the single time-off data if it's loaded
    if (state.singleTimeOff.data?.id === payload.id) {
      state.singleTimeOff.data = payload
    }
  },
  [updateTimeOffFailed.type]: (state) => {
    setFailed(state.updateTimeOff)
  },

  [deleteTimeOffRequested.type]: (state) => {
    setRequested(state.deleteTimeOff)
  },
  [deleteTimeOffReceived.type]: (state, { payload }) => {
    setReceived(state.deleteTimeOff)
    // Remove the time-off from the list
    if (Array.isArray(state.timeOff.data)) {
      state.timeOff.data = state.timeOff.data.filter((item) => item.id !== payload.id)
    }
  },
  [deleteTimeOffFailed.type]: (state) => {
    setFailed(state.deleteTimeOff)
  },

  // ========================================
  // Assigned Experiences
  // ========================================
  [assignedExperiencesRequested.type]: (state) => {
    setRequested(state.assignedExperiences)
  },
  [assignedExperiencesReceived.type]: (state, { payload }) => {
    setReceived(state.assignedExperiences)
    state.assignedExperiences.data = payload.data
    Object.assign(state.assignedExperiences.metadata, payload.meta)
  },
  [assignedExperiencesFailed.type]: (state) => {
    setFailed(state.assignedExperiences)
  },

  [assignExperienceRequested.type]: (state) => {
    setRequested(state.assignExperience)
  },
  [assignExperienceReceived.type]: (state, { payload }) => {
    setReceived(state.assignExperience)
    // Add new experience to the list
    state.assignedExperiences.data.push(payload)
  },
  [assignExperienceFailed.type]: (state) => {
    setFailed(state.assignExperience)
  },

  [removeExperienceRequested.type]: (state) => {
    setRequested(state.removeExperience)
  },
  [removeExperienceReceived.type]: (state, { payload }) => {
    setReceived(state.removeExperience)
    // Remove experience from the list
    state.assignedExperiences.data = state.assignedExperiences.data.filter((item) => item.trip_assignment_id !== payload.id)
  },
  [removeExperienceFailed.type]: (state) => {
    setFailed(state.removeExperience)
  },

  [checkTripBookingConflictsRequested.type]: (state) => {
    setRequested(state.tripBookingConflicts)
  },
  [checkTripBookingConflictsReceived.type]: (state, { payload }) => {
    setReceived(state.tripBookingConflicts)
    state.tripBookingConflicts.data = payload
  },
  [checkTripBookingConflictsFailed.type]: (state) => {
    setFailed(state.tripBookingConflicts)
  },

  // ========================================
  // Documents (Licenses & Certifications)
  // ========================================
  [documentsRequested.type]: (state) => {
    setRequested(state.documents)
  },
  [documentsReceived.type]: (state, { payload }) => {
    setReceived(state.documents)
    state.documents.data = payload
  },
  [documentsFailed.type]: (state) => {
    setFailed(state.documents)
  },

  [singleDocumentRequested.type]: (state) => {
    setRequested(state.singleDocument)
  },
  [singleDocumentReceived.type]: (state, { payload }) => {
    setReceived(state.singleDocument)
    state.singleDocument.data = payload
  },
  [singleDocumentFailed.type]: (state) => {
    setFailed(state.singleDocument)
  },

  [createDocumentRequested.type]: (state) => {
    setRequested(state.createDocument)
  },
  [createDocumentReceived.type]: (state, { payload }) => {
    setReceived(state.createDocument)
    // Add new document to the list
    state.documents.data.unshift(payload)
  },
  [createDocumentFailed.type]: (state) => {
    setFailed(state.createDocument)
  },

  [updateDocumentRequested.type]: (state) => {
    setRequested(state.updateDocument)
  },
  [updateDocumentReceived.type]: (state, { payload }) => {
    setReceived(state.updateDocument)
    // Update the document in the list
    const index = state.documents.data.findIndex((item) => item.id === payload.id)
    if (index !== -1) {
      state.documents.data[index] = payload
    }
    // Also update the single document data if it's loaded
    if (state.singleDocument.data?.id === payload.id) {
      state.singleDocument.data = payload
    }
  },
  [updateDocumentFailed.type]: (state) => {
    setFailed(state.updateDocument)
  },

  [deleteDocumentRequested.type]: (state) => {
    setRequested(state.deleteDocument)
  },
  [deleteDocumentReceived.type]: (state, { payload }) => {
    setReceived(state.deleteDocument)
    // Remove the document from the list
    state.documents.data = state.documents.data.filter((item) => item.id !== payload.id)
  },
  [deleteDocumentFailed.type]: (state) => {
    setFailed(state.deleteDocument)
  },

  // ========================================
  // Deactivation/Deletion Operations
  // ========================================
  [checkDeactivationRequested.type]: (state) => {
    setRequested(state.checkDeactivation)
  },
  [checkDeactivationReceived.type]: (state, { payload }) => {
    setReceived(state.checkDeactivation)
    state.checkDeactivation.data = payload
  },
  [checkDeactivationFailed.type]: (state) => {
    setFailed(state.checkDeactivation)
  },

  [checkDeletionRequested.type]: (state) => {
    setRequested(state.checkDeletion)
  },
  [checkDeletionReceived.type]: (state, { payload }) => {
    setReceived(state.checkDeletion)
    state.checkDeletion.data = payload
  },
  [checkDeletionFailed.type]: (state) => {
    setFailed(state.checkDeletion)
  },

  [deactivateCrewRequested.type]: (state) => {
    setRequested(state.deactivateCrew)
  },
  [deactivateCrewReceived.type]: (state, { payload }) => {
    setReceived(state.deactivateCrew)
    // Update the profile status to deactivated if it's loaded
    if (state.profile.data) {
      state.profile.data.status = "deactivated"
    }
    // Update in the list as well
    const index = state.items.findIndex((item) => item.id === payload?.id || item.id === state.profile.data?.id)
    if (index !== -1) {
      state.items[index].status = "deactivated"
    }
  },
  [deactivateCrewFailed.type]: (state) => {
    setFailed(state.deactivateCrew)
  },

  [activateCrewRequested.type]: (state) => {
    setRequested(state.activateCrew)
  },
  [activateCrewReceived.type]: (state, { payload }) => {
    setReceived(state.activateCrew)
    // Update the profile status to active if it's loaded
    if (state.profile.data) {
      state.profile.data.status = "active"
    }
    // Update in the list as well
    const index = state.items.findIndex((item) => item.id === payload?.id || item.id === state.profile.data?.id)
    if (index !== -1) {
      state.items[index].status = "active"
    }
  },
  [activateCrewFailed.type]: (state) => {
    setFailed(state.activateCrew)
  },

  [deleteCrewRequested.type]: (state) => {
    setRequested(state.deleteCrew)
  },
  [deleteCrewReceived.type]: (state, { payload }) => {
    setReceived(state.deleteCrew)
    // Remove from the list
    state.items = state.items.filter((item) => item.id !== payload?.id && item.id !== state.profile.data?.id)
  },
  [deleteCrewFailed.type]: (state) => {
    setFailed(state.deleteCrew)
  }
})

export default crewProfilesReducer

// ========================================
// HELPERS
// ========================================

// Helper to map frontend status to backend status
const mapStatusToBackend = (status) => {
  if (!status || status === "all") return "all"

  const statusMap = {
    available: "active",
    disabled: "deactivated"
  }

  return statusMap[status] || status
}

// ========================================
// API ACTIONS - Crew Profiles List
// ========================================

export const getCrewProfiles = (params = {}) => {
  const { status, roleIds, search, page = 1, per_page = 10, sort_by, sort_direction, available_on, ...otherParams } = params

  // Build base URL params
  const urlParams = new URLSearchParams()

  // Add pagination
  urlParams.append("page", page)
  urlParams.append("per_page", per_page)

  // Add status filter (map frontend to backend)
  if (status && status !== "all") {
    urlParams.append("status", mapStatusToBackend(status))
  }

  // Add role filter - append each role ID separately
  if (roleIds && roleIds.length > 0) {
    roleIds.forEach((roleId) => {
      urlParams.append("role_ids[]", roleId)
    })
  }

  // Add search filter
  if (search && search.trim()) {
    urlParams.append("search", search.trim())
  }

  // Add sorting parameters
  if (sort_by) {
    urlParams.append("sort_by", sort_by)
    urlParams.append("sort_direction", sort_direction || "asc")
  }

  // Add available_on date filter (YYYY-MM-DD format)
  if (available_on) {
    urlParams.append("available_on", available_on)
  }

  // Add any other params
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, value)
    }
  })

  return api({
    url: `/guide/profiles?${urlParams.toString()}`,
    method: "get",
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })
}

export const cleanCrewProfiles = () => (dispatch) => dispatch(cleaned())

// ========================================
// API ACTIONS - Single Profile
// ========================================

export const getCrewProfile = (id) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "get",
    onStart: profileRequested.type,
    onSuccess: profileReceived.type,
    onError: profileFailed.type
  })

export const updateCrewProfile = (id, formData) =>
  api({
    url: `/guide/profiles/${id}`,
    method: "put",
    data: formData,
    onStart: updateRequested.type,
    onSuccess: updateReceived.type,
    onError: updateFailed.type
  })

// ========================================
// API ACTIONS - Staff Bookings
// ========================================

export const getCrewBookings = (profileId, params = {}) => {
  const { status, start_date, end_date, sort_by, sort_order, ...otherParams } = params

  const urlParams = new URLSearchParams()

  // Add filters
  if (status) {
    urlParams.append("status", status)
  }
  if (start_date) {
    urlParams.append("start_date", start_date)
  }
  if (end_date) {
    urlParams.append("end_date", end_date)
  }
  if (sort_by) {
    urlParams.append("sort_by", sort_by)
  }
  if (sort_order) {
    urlParams.append("sort_order", sort_order)
  }

  // Add any other params
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, value)
    }
  })

  return api({
    url: `/guide/profiles/${profileId}/bookings?${urlParams.toString()}`,
    method: "get",
    onStart: bookingsRequested.type,
    onSuccess: bookingsReceived.type,
    onError: bookingsFailed.type
  })
}

// ========================================
// API ACTIONS - Availability
// ========================================

export const getCrewAvailability = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/availability`,
    method: "get",
    onStart: availabilityRequested.type,
    onSuccess: availabilityReceived.type,
    onError: availabilityFailed.type
  })

export const updateCrewAvailability = (profileId, data) =>
  api({
    url: `/guide/profiles/${profileId}/availability`,
    method: "put",
    data: { availability: data },
    onStart: availabilityUpdateRequested.type,
    onSuccess: availabilityUpdateReceived.type,
    onError: availabilityUpdateFailed.type
  })

// ========================================
// API ACTIONS - Scheduled Time Off
// ========================================

export const getCrewTimeOff = (profileId, params = {}) => {
  const { time_frame = "upcoming", status, start_date, end_date, page = 1, per_page = 20, ...otherParams } = params

  const urlParams = new URLSearchParams()

  // Add pagination
  urlParams.append("page", page)
  urlParams.append("per_page", per_page)

  // Add filters
  urlParams.append("time_frame", time_frame)
  if (status) {
    urlParams.append("status", status)
  }
  if (start_date) {
    urlParams.append("start_date", start_date)
  }
  if (end_date) {
    urlParams.append("end_date", end_date)
  }

  // Add any other params
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlParams.append(key, value)
    }
  })

  return api({
    url: `/guide/profiles/${profileId}/time_off?${urlParams.toString()}`,
    method: "get",
    onStart: timeOffRequested.type,
    onSuccess: timeOffReceived.type,
    onError: timeOffFailed.type
  })
}

export const getSingleCrewTimeOff = (profileId, timeOffId) =>
  api({
    url: `/guide/profiles/${profileId}/time_off/${timeOffId}`,
    method: "get",
    onStart: singleTimeOffRequested.type,
    onSuccess: singleTimeOffReceived.type,
    onError: singleTimeOffFailed.type
  })

export const createCrewTimeOff = (profileId, data) =>
  api({
    url: `/guide/profiles/${profileId}/time_off`,
    method: "post",
    data: { time_off_request: data },
    onStart: createTimeOffRequested.type,
    onSuccess: createTimeOffReceived.type,
    onError: createTimeOffFailed.type
  })

export const updateCrewTimeOff = (profileId, timeOffId, data) =>
  api({
    url: `/guide/profiles/${profileId}/time_off/${timeOffId}`,
    method: "put",
    data: { time_off_request: data },
    onStart: updateTimeOffRequested.type,
    onSuccess: updateTimeOffReceived.type,
    onError: updateTimeOffFailed.type
  })

export const deleteCrewTimeOff = (profileId, timeOffId) =>
  api({
    url: `/guide/profiles/${profileId}/time_off/${timeOffId}`,
    method: "delete",
    onStart: deleteTimeOffRequested.type,
    onSuccess: deleteTimeOffReceived.type,
    onError: deleteTimeOffFailed.type,
    props: { id: timeOffId }
  })

// ========================================
// API ACTIONS - Assigned Experiences
// ========================================

export const getAssignedExperiences = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/assigned_experiences`,
    method: "get",
    onStart: assignedExperiencesRequested.type,
    onSuccess: assignedExperiencesReceived.type,
    onError: assignedExperiencesFailed.type
  })

export const assignTripToCrewMember = (profileId, data) =>
  api({
    url: `/guide/profiles/${profileId}/trip_assignments`,
    method: "post",
    data: { trip_assignment: data },
    onStart: assignExperienceRequested.type,
    onSuccess: assignExperienceReceived.type,
    onError: assignExperienceFailed.type
  })

export const removeTripFromCrewMember = (profileId, assignmentId) =>
  api({
    url: `/guide/profiles/${profileId}/trip_assignments/${assignmentId}`,
    method: "delete",
    onStart: removeExperienceRequested.type,
    onSuccess: removeExperienceReceived.type,
    onError: removeExperienceFailed.type,
    props: { id: assignmentId }
  })

export const checkTripBookingConflicts = (tripId) =>
  api({
    url: `/guide/trips/${tripId}/check_booking_conflicts`,
    method: "get",
    onStart: checkTripBookingConflictsRequested.type,
    onSuccess: checkTripBookingConflictsReceived.type,
    onError: checkTripBookingConflictsFailed.type
  })

// ========================================
// API ACTIONS - Documents (Licenses & Certifications)
// ========================================

export const getCrewDocuments = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/documents`,
    method: "get",
    onStart: documentsRequested.type,
    onSuccess: documentsReceived.type,
    onError: documentsFailed.type
  })

export const getSingleCrewDocument = (profileId, documentId) =>
  api({
    url: `/guide/profiles/${profileId}/documents/${documentId}`,
    method: "get",
    onStart: singleDocumentRequested.type,
    onSuccess: singleDocumentReceived.type,
    onError: singleDocumentFailed.type
  })

export const createCrewDocument = (profileId, formData) =>
  api({
    url: `/guide/profiles/${profileId}/documents`,
    method: "post",
    data: formData,
    onStart: createDocumentRequested.type,
    onSuccess: createDocumentReceived.type,
    onError: createDocumentFailed.type
  })

export const updateCrewDocument = (profileId, documentId, formData) =>
  api({
    url: `/guide/profiles/${profileId}/documents/${documentId}`,
    method: "put",
    data: formData,
    onStart: updateDocumentRequested.type,
    onSuccess: updateDocumentReceived.type,
    onError: updateDocumentFailed.type
  })

export const deleteCrewDocument = (profileId, documentId) =>
  api({
    url: `/guide/profiles/${profileId}/documents/${documentId}`,
    method: "delete",
    onStart: deleteDocumentRequested.type,
    onSuccess: deleteDocumentReceived.type,
    onError: deleteDocumentFailed.type,
    props: { id: documentId }
  })

// ========================================
// API ACTIONS - Deactivation/Deletion Operations
// ========================================

/**
 * Check if crew member can be deactivated and get details
 * @param {number} profileId - Crew profile ID
 * @returns {Promise} API response with deactivation details
 *
 * Response structure matches mock data:
 * {
 *   success: boolean,
 *   can_deactivate: boolean,
 *   status: string, // "no_experiences_assigned" | "no_active_bookings" | "fully_substitutable" | "partially_substitutable"
 *   crew_id: number,
 *   crew_name: string,
 *   role: string,
 *   assigned_trips: [{ trip_id, trip_name, trip_category, guide_name, min_guests, max_guests }],
 *   active_bookings: [{
 *     booking_id, booking_uuid, experience_name, client_name, booked_date, start_time, end_time,
 *     status, guest_count, available_substitutes: [{ crew_id, crew_name, role }],
 *     can_substitute: boolean
 *   }],
 *   requires_substitution: boolean,
 *   exp_message: string,
 *   booking_message: string,
 *   blocking_bookings: [] // Present when can_deactivate is false
 * }
 *
 * Endpoint: GET /api/guide/profiles/:id/check_operation?operation=deactivate
 */
export const checkCrewDeactivation = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/check_operation?operation=deactivate&profile_type=crew`,
    method: "get",
    onStart: checkDeactivationRequested.type,
    onSuccess: checkDeactivationReceived.type,
    onError: checkDeactivationFailed.type
  })

/**
 * Check if crew member can be deleted and get details
 * @param {number} profileId - Crew profile ID
 * @returns {Promise} API response with deletion details
 *
 * Response structure similar to deactivation check
 *
 * Endpoint: GET /api/guide/profiles/:id/check_operation?operation=delete
 */
export const checkCrewDeletion = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/check_operation?operation=delete&profile_type=crew`,
    method: "get",
    onStart: checkDeletionRequested.type,
    onSuccess: checkDeletionReceived.type,
    onError: checkDeletionFailed.type
  })

/**
 * Deactivate a crew member
 * @param {number} profileId - Crew profile ID
 * @param {Object} data - Deactivation data
 * @param {Object} data.substitutions - Optional: Mapping of booking_id to substitute crew_id
 *   Example: { "12345678": 42, "12345679": 43 }
 * @returns {Promise} API response
 *
 * Endpoint: POST /api/guide/profiles/:id/deactivate (to be confirmed)
 */
export const deactivateCrewMember = (profileId, data = {}) =>
  api({
    url: `/guide/profiles/${profileId}/deactivate`,
    method: "post",
    data: { ...data, profile_type: "crew" },
    onStart: deactivateCrewRequested.type,
    onSuccess: deactivateCrewReceived.type,
    onError: deactivateCrewFailed.type
  })

/**
 * Activate a crew member
 * @param {number} profileId - Crew profile ID
 * @returns {Promise} API response
 *
 * Endpoint: POST /api/guide/profiles/:id/activate
 */
export const activateCrewMember = (profileId) =>
  api({
    url: `/guide/profiles/${profileId}/reactivate`,
    method: "post",
    onStart: activateCrewRequested.type,
    onSuccess: activateCrewReceived.type,
    onError: activateCrewFailed.type
  })

/**
 * Delete a crew member
 * @param {number} profileId - Crew profile ID
 * @param {Object} data - Deletion data
 * @param {Object} data.substitutions - Optional: Mapping of booking_id to substitute crew_id
 * @returns {Promise} API response
 *
 * Endpoint: DELETE /api/guide/profiles/:id (to be confirmed)
 */
export const deleteCrewMember = (profileId, data = {}) =>
  api({
    url: `/guide/profiles/${profileId}`,
    method: "delete",
    data: { ...data, profile_type: "crew" },
    onStart: deleteCrewRequested.type,
    onSuccess: deleteCrewReceived.type,
    onError: deleteCrewFailed.type
  })
